import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@mui/styles";
import {
  Card,
  CardContent,
  FormControl,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";

import AppContext from "../../../context/AppContext";
import { getJobs } from "../../../services/JobService";
import { getAllOrders } from "../../../services/OrderService";
import { getProjects } from "../../../services/ProjectService";

import Autocomplete from "../../../components/Autocomplete";
import OrdersTable from "../../../components/CustomTable/OrdersTable";

const useStyles = makeStyles({
  root: {},
});

export default function AdminOrders() {
  const classes = useStyles();
  const { jobs, projects, setJobs, setProjects } = useContext(AppContext);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [orders, setOrders] = useState([]);
  const [jobUnits, setJobUnits] = useState([]);
  const [selectedStatusFilter, setSelectedStatusFilter] =
    useState("in-progress");

  const filteredOrders = useMemo(() => {
    return (
      orders
        .filter(
          (item) => !selectedProject || item.PRO_ID === selectedProject.PRO_ID
        )
        .filter((item) => !selectedJob || item.JOB_ID === selectedJob.JOB_ID)
        .filter(
          (item) => !selectedUnit || item.UNIT_ID === parseInt(selectedUnit)
        )
        .filter(
          (item) =>
            !selectedStatusFilter || item.STATUS === selectedStatusFilter
        ) || []
    );
  }, [
    orders,
    selectedProject,
    selectedJob,
    selectedUnit,
    selectedStatusFilter,
  ]);

  useEffect(() => {
    const newJobUnits = [
      ...new Set(filteredOrders.map((item) => `${item.UNIT_ID}`)),
    ];

    if (newJobUnits.length === 1) setSelectedUnit(newJobUnits[0]);

    setJobUnits(newJobUnits);
  }, [filteredOrders]);

  const getJobsFromProject = useCallback(
    (proId) => {
      return jobs.filter((job) => proId === "" || job.PRO_ID === proId);
    },
    [jobs]
  );

  const retrieveProjects = useCallback(async () => {
    try {
      setLoadingProjects(true);
      const newProjects = await getProjects();
      setProjects(newProjects);
      setLoadingProjects(false);
    } catch (e) {
      console.error(e);
      toast.error("Unexpected error occurs while loading projects!");
    }
  }, [setProjects]);

  const retrieveJobs = useCallback(async () => {
    try {
      setLoadingJobs(true);
      const newJobs = await getJobs();
      setJobs(newJobs);
      setLoadingJobs(false);
    } catch (e) {
      console.error(e);
      toast.error("Unexpected error occurs while loading jobs!");
    }
  }, [setJobs]);

  const retrieveAllOrders = useCallback(async () => {
    try {
      setLoadingOrders(true);
      const res = await getAllOrders();
      const refinedOrders = res.reduce((acc, order) => {
        const key = `${order.PRO_ID}_${order.JOB_ID}_${order.UNIT_ID}`;

        if (!acc[key]) {
          acc[key] = {
            PRO_ID: order.PRO_ID,
            PROJECT_DESCRIPTION: order.PROJECT_DESCRIPTION,
            JOB_ID: order.JOB_ID,
            JOB_DESC: order.JOB_DESC,
            UNIT_ID: order.UNIT_ID,
            FOREMAN: order.FOREMAN,
            ORDERS: [],
          };
        }

        acc[key].ORDERS.push(order);

        return acc; // Return the accumulator for the next iteration
      }, {});
      const newOrders = Object.values(refinedOrders).map((order) => ({
        ...order,
        STATUS:
          order.ORDERS.filter((item) => item.PO_CREATE_DATE === null).length > 0
            ? "in-progress"
            : "closed",
      }));

      setOrders(newOrders);
      setLoadingOrders(false);
    } catch (e) {
      console.error(e);
      toast.error("Unexpected error occurs while loading job orders!");
    }
  }, []);

  useEffect(() => {
    retrieveAllOrders();
    retrieveProjects();
    retrieveJobs();
  }, []);

  const handleProjectChange = (e, newValue) => {
    setSelectedProject(newValue);
    setSelectedJob(null);
    setSelectedUnit(null);
  };

  const handleJobChange = (e, newValue) => {
    setSelectedJob(newValue);
    setSelectedUnit(null);
  };

  const handleUnitChange = (e, newValue) => {
    setSelectedUnit(newValue);
  };

  const handleUpdate = () => {
    retrieveAllOrders();
  };

  const handleStatusFilterChange = (e) => {
    setSelectedStatusFilter(e.target.value);
  };

  return (
    <div className={classes.root}>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Grid
                container
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
              >
                <Grid item md={8} xs={12}>
                  <Grid container alignItems="center" spacing={2}>
                    <Grid item xs={4}>
                      <FormControl fullWidth>
                        <Autocomplete
                          label="Project"
                          variant="outlined"
                          size="small"
                          loading={loadingProjects}
                          disabled={loadingProjects}
                          options={projects || []}
                          getOptionLabel={(option) => option.DESCRIPTION}
                          isOptionEqualToValue={(option, value) =>
                            option.PRO_ID === value.PRO_ID
                          }
                          value={selectedProject}
                          onChange={handleProjectChange}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={4}>
                      <FormControl fullWidth>
                        <Autocomplete
                          label="Job"
                          variant="outlined"
                          size="small"
                          loading={loadingJobs}
                          disabled={loadingJobs || !selectedProject}
                          options={
                            getJobsFromProject(selectedProject?.PRO_ID) || []
                          }
                          getOptionLabel={(option) => option.JOB_DESC}
                          isOptionEqualToValue={(option, value) =>
                            option.JOB_ID === value.JOB_ID
                          }
                          value={selectedJob}
                          onChange={handleJobChange}
                        />
                      </FormControl>
                    </Grid>
                    {selectedJob && jobUnits.length > 1 && (
                      <Grid item xs={4}>
                        <FormControl fullWidth>
                          <Autocomplete
                            label="Unit"
                            variant="outlined"
                            size="small"
                            loading={loadingOrders}
                            disabled={
                              loadingOrders || !selectedProject || !selectedJob
                            }
                            options={jobUnits || []}
                            getOptionLabel={(option) => option}
                            isOptionEqualToValue={(option, value) =>
                              parseInt(option) === parseInt(value)
                            }
                            value={selectedUnit}
                            onChange={handleUnitChange}
                          />
                        </FormControl>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
                <Grid item md={4} xs={12}>
                  <Grid container justifyContent="flex-end">
                    <Grid item>
                      <ToggleButtonGroup
                        size="small"
                        aria-label="Change Request Status"
                        value={selectedStatusFilter}
                        onChange={handleStatusFilterChange}
                        exclusive
                      >
                        <ToggleButton value="">All</ToggleButton>
                        <ToggleButton color="primary" value="in-progress">
                          In progress
                        </ToggleButton>
                        <ToggleButton color="error" value="closed">
                          Closed
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Grid container spacing={4}>
                <Grid item xs={12}>
                  <OrdersTable
                    title="Orders"
                    loading={loadingOrders}
                    items={filteredOrders}
                    handleUpdate={handleUpdate}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </div>
  );
}
